import { clamp, DEG, unit } from './vec3.js';
/**
 * What a single gravity sample says, for every block that reads an accelerometer.
 *
 * These lived inside `rotation-tracking.ts`, which was their only consumer while Rotation was the
 * only such block. The tracker now imports them, which is the right way round: a static reading is
 * the primitive, and integrating a PATH out of successive readings is the thing built on top.
 *
 * ## The teacher declares nothing, and neither does the student
 *
 * An earlier draft made the teacher choose which phone edge was being measured. That was wrong:
 * a clinometer that fails when the phone is turned is not a clinometer, and a bubble level asks
 * nothing. What replaced it is `gravityRollDegrees`, which takes its axis from the SCREEN rather
 * than from a setting — the phone already knows which way round it is being held, the same way a
 * bubble level app rotates its own interface.
 */
/**
 * The plain clinometer reading: the angle between the screen normal and up, in [0, 180].
 *
 * 0 is flat with the screen up, 90 is on edge, 180 is flat with the screen down. Tilting away
 * from flat in ANY direction increases it by the same amount, which is what "any orientation"
 * means in practice: a teacher who turns the phone a quarter on the ramp reads the same angle.
 *
 * Exported because it is the correct and complete answer for a POSE, and because Tilt's display
 * and Rotation's pre-latch fallback must agree exactly rather than approximate each other.
 */
export function gravityTiltDegrees(g) {
    const u = unit(g);
    if (!u)
        return null;
    return Math.acos(clamp(-u.z, -1, 1)) * DEG;
}
/**
 * Gravity's direction within the screen plane, expressed so that 0 means the phone's long axis is
 * HORIZONTAL, rising through 90 when it is vertical.
 *
 * Rotation's sweep reading. The zero point agrees with the tilt gesture's: in both cases 0 means
 * the feature being measured is horizontal, so the same authored answer means one physical thing.
 */
export function gravityInPlaneDegrees(g) {
    const u = unit(g);
    if (!u)
        return null;
    // Degenerate when gravity is along the screen normal: both in-plane components vanish and the
    // direction is noise. That is the flat posture, where this quantity is meaningless anyway.
    if (Math.hypot(u.x, u.y) < 1e-6)
        return null;
    return ((90 - Math.atan2(-u.x, -u.y) * DEG) % 360 + 360) % 360;
}
/**
 * The gravity sample a phone would produce at this tilt — the inverse of `gravityTiltDegrees`.
 *
 * Exists so the simulator drives the SAME arithmetic a real phone does rather than echoing a
 * number back. `spin` turns the phone on its own face, and the fact that it changes NOTHING about
 * the reading is the property worth being able to demonstrate: a teacher can set it and watch the
 * number hold still.
 */
export function tiltGravityFor(degrees, spin = 0) {
    const d = degrees * (Math.PI / 180), s = spin * (Math.PI / 180);
    const sin = Math.sin(d), cos = Math.cos(d);
    return { x: -sin * Math.sin(s), y: -sin * Math.cos(s), z: -cos };
}
/**
 * Which device axis is horizontal ACROSS the screen, as the phone is currently being held.
 *
 * `window.screen.orientation.angle` is 0 in the phone's natural orientation and 90 or 270 when it
 * has been turned on its side. In portrait the screen's left-right is the device's +x; a quarter
 * turn makes it the device's y, with the sign following which way it was turned.
 *
 * Returned as a unit vector in device coordinates rather than as an enum, so the reading below is
 * one formula instead of a four-way switch — and so a tablet reporting 180 is handled by the same
 * arithmetic rather than by a case nobody remembered to write.
 */
export function screenRightAxis(angleDegrees) {
    const a = (((angleDegrees % 360) + 360) % 360) * (Math.PI / 180);
    return { x: Math.cos(a), y: -Math.sin(a) };
}
/** The device axis pointing UP the screen, as it is currently held. Perpendicular to
 *  `screenRightAxis`, and the axis a forward/back lean is about. */
export function screenUpAxis(angleDegrees) {
    const r = screenRightAxis(angleDegrees);
    return { x: -r.y, y: r.x };
}
/**
 * The lean a phone is being held at, taking its own two axes against the screen.
 *
 * ## Two components, and the bigger one is the measurement
 *
 *   ROLL   gravity along the screen's RIGHT — a left/right lean, about the long axis
 *   PITCH  gravity along the screen's UP    — a forward/back lean, about the short axis
 *
 * A slope running left-right across the screen lands entirely in roll; turn the phone a quarter
 * and it lands entirely in pitch. Taking whichever is LARGER is therefore what makes portrait and
 * landscape the same measurement, with no setting and no special case — the thing a clinometer
 * has to do and the reason an earlier draft's single named axis was wrong.
 *
 * ## The smaller one is the warning
 *
 * Held diagonally, a slope splits between the two and BOTH shrink: a 30 degree slope at 45 degrees
 * reads 20.7 on each. The reading is then a plausible, quiet understatement, which is the worst
 * kind of wrong. The smaller component is exactly how much is being lost, so it is reported
 * alongside rather than discarded, and the block warns on it.
 *
 * That threshold also protects the only discontinuity here. Which component is "bigger" swaps at
 * 45 degrees of azimuth — deep inside the warned band — so the measurement can never change axis
 * without the student having been told the hold is wrong.
 */
export function gravityLeanDegrees(g, screenAngleDegrees = 0) {
    const u = unit(g);
    if (!u)
        return null;
    const right = screenRightAxis(screenAngleDegrees);
    const up = screenUpAxis(screenAngleDegrees);
    const roll = Math.asin(clamp(u.x * right.x + u.y * right.y, -1, 1)) * DEG;
    const pitch = Math.asin(clamp(u.x * up.x + u.y * up.y, -1, 1)) * DEG;
    return Math.abs(roll) >= Math.abs(pitch)
        ? { degrees: roll, offAxisDegrees: Math.abs(pitch) }
        : { degrees: pitch, offAxisDegrees: Math.abs(roll) };
}
/**
 * How far a phone may lean along the axis it is NOT measuring before the reading stops being
 * trustworthy.
 *
 * 8 degrees of off-axis costs about 1 degree of the measurement on a 30 degree slope, which is
 * below what anyone is reading off a phone. It rises steeply after that: 14 off-axis costs 4, and
 * 21 costs 9.
 */
export const TILT_OFF_AXIS_CUTOFF_DEG = 8;
/** The gravity sample a phone leaning this far to the right would produce, in its natural
 *  orientation. The inverse of `gravityRollDegrees`, for the simulator. */
export function rollGravityFor(degrees) {
    const d = degrees * (Math.PI / 180);
    return { x: Math.sin(d), y: 0, z: -Math.cos(d) };
}
//# sourceMappingURL=gravity-readings.js.map